import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { useRootStore } from 'store';
import { Card, Row } from 'react-bootstrap';
import {
  CellFloatCustom,
  CellLeft,
  FlexRow,
  FormFilterCard,
  TableInMem,
  YupStringReq,
  YupNumericReq,
  YupNumeric,
  exportXlsModal,
  FormCol4,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import * as Yup from 'yup';
import { FieldSelectContributor } from 'shared-pages/components';

import gql from 'graphql-tag';
import { SelectContributionYear } from '../components/SelectContributionYear';
import { formatFloat } from 'shared-pages/utils/helpers';
import { useTranslation } from 'react-i18next';

export const gqlGetSumOfContributionsItem = gql`
  query getSumOfContributionsItem($contributorId: Int, $year: Int) {
    getSumOfContributionsItem(contributorId: $contributorId, year: $year) {
      data {
        itemId
        groupName
        subgroupName
        itemName
        qty1
        units1
        qty2
        units2
        price
      }
      recordCount
    }
  }
`;

/* ----------- Page ------------------ */
export const YearlyContributionsPage = () => {
  const { appStore } = useRootStore();
  const client = useApolloClient();
  const { t, i18n } = useTranslation();

  /* ----------------- Route State -------------------------- */
  const location = useLocation<any>();
  const history = useHistory();
  // Στο state του routing αποθηκεύω:
  //    1. την τρέχουσα σελίδα του table pagination (pageIndex)
  //  & 2. τα values των φίλτρων (filterValues)

  /// Αρχικές Τιμές Στα Φίλτρα
  useEffect(() => {
    const values = location?.state?.filterValues ?? {};
    if (!values.year) {
      values.year = new Date().getFullYear();
      // Ενημέρωσε το State του Route
      history.replace(location.pathname, {
        ...location.state,
        filterValues: values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [queryParams, setQueryParams] = useState<{
    year: number;
    contributorId?: number;
  }>(() => {
    const yearFromRoute = parseInt(location?.state?.filterValues?.year, 10);
    const contributorFromRoute = parseInt(location?.state?.filterValues?.contributorId, 10);
    return {
      year: Number.isFinite(yearFromRoute) ? yearFromRoute : new Date().getFullYear(),
      ...(Number.isFinite(contributorFromRoute) ? { contributorId: contributorFromRoute } : {}),
    };
  });

  const [footerValues, setFooterValues] = useState({
    qty1: 0,
    qty2: 0,
    price: 0,
  });

  const [isFetching, setIsFetching] = useState(false);

  const [getData, { called, data: dataRaw }] = useLazyQuery(gqlGetSumOfContributionsItem, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const data = dataRaw?.getSumOfContributionsItem?.data;
  const recordCount = dataRaw?.getSumOfContributionsItem?.recordCount;

  useEffect(() => {
    const items = dataRaw?.getSumOfContributionsItem?.data;
    if (!items) {
      return;
    }
    let qty1 = 0;
    let qty2 = 0;
    let price = 0;
    for (let index = 0; index < items.length; index++) {
      const values = items[index];
      qty1 += values?.qty1 ?? 0;
      qty2 += values?.qty2 ?? 0;
      price += values?.price ?? 0;
    }
    setFooterValues({ qty1, qty2, price });
  }, [dataRaw]);

  const filtersChanged = useCallback(
    (values) => {
      const year = parseInt(values.year, 10);
      if (!Number.isFinite(year)) {
        return;
      }

      const newQueryParams: { year: number; contributorId?: number } = { year };
      if (!appStore.isContributor) {
        const contributorId = parseInt(values.contributorId, 10);
        if (!Number.isFinite(contributorId)) {
          return;
        }
        newQueryParams.contributorId = contributorId;
      }

      setQueryParams(newQueryParams);
      setIsFetching(true);
      getData({ variables: newQueryParams }).finally(() => setIsFetching(false));
    },
    [appStore.isContributor, getData],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetSumOfContributionsItem);
    //return [null, { loading: null, data: null }];
  }
  const handleExport = useCallback(async () => {
    const filename = queryParams.contributorId
      ? `Ετήσιες Εισφορές ${queryParams.year} ${queryParams.contributorId}`
      : `Ετήσιες Εισφορές ${queryParams.year}`;
    await exportXlsModal({
      apolloClient: client,
      tableName: undefined,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: filename,
      columnsDef: [
        { fieldName: 'groupName', fieldTitle: 'Κατηγορία 1', fieldType: 'TEXT' },
        { fieldName: 'subgroupName', fieldTitle: 'Κατηγορία 2', fieldType: 'TEXT' },
        { fieldName: 'itemName', fieldTitle: 'Κατηγορία 3', fieldType: 'TEXT' },
        { fieldName: 'qty1', fieldTitle: 'Ποσότητα 1', fieldType: 'FLOAT' },
        { fieldName: 'units1', fieldTitle: 'Μονάδες 1', fieldType: 'TEXT' },
        { fieldName: 'qty2', fieldTitle: 'Ποσότητα 2', fieldType: 'FLOAT' },
        { fieldName: 'units2', fieldTitle: 'Μονάδες 2', fieldType: 'TEXT' },
        { fieldName: 'price', fieldTitle: 'Εισφορά σε ευρώ', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'groupName',
        Header: t('Πίνακας'),
        Cell: CellLeft,
      },
      /* {
        accessor: 'subgroupName',
        Header: 'Κατηγορία 2',
        Cell: CellLeft,
      }, */
      {
        accessor: 'itemName',
        Header: t('Κατηγορία'),
        Cell: CellLeft,
      },
      {
        accessor: 'qty1',
        Header: t('Ποσότητα (ΤΜΧ)'),
        Cell: ({ row }) => <div className="text-center">{formatFloat(row.original.qty1, 2)}</div>,
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty1, 2)}</div>;
        },
      },
      {
        accessor: 'qty2',
        Header: t('Βάρος (kg)'),
        Cell: ({ row }) => <div className="text-center">{formatFloat(row.original.qty2, 2)}</div>,
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty2, 2)}</div>;
        },
      },
      {
        accessor: 'price',
        Header: t('Εισφορά (€)'),
        Cell: CellFloatCustom(5, 2),
        Footer: () => {
          return CellFloatCustom(5, 2)({ cell: { value: footerValues.price } });
        },
      },
    ],
    [footerValues.price, footerValues.qty1, footerValues.qty2],
  );
  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Ετήσια Αθροίσματα Εισφορών')}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        {/* Φίλτρα */}
        <Filters onChange={filtersChanged} initialValues={location?.state?.filterValues ?? {}} />
        {/* TableGrid */}
        {called && data ? (
          <TableInMem
            onExport={handleExport}
            columns={columns}
            data={data}
            loading={isFetching}
            hasSearch={false}
            hasFooter={true}
            propPageSize={50}
          />
        ) : (
          <LoadingOverlay active={isFetching} spinner text={t('Παρακαλώ περιμένετε...')}>
            <div className="empty-table-container" />
          </LoadingOverlay>
        )}
      </Card.Body>
    </Card>
  );
};

/* ----------- Filters ------------------ */
const Filters = ({ onChange, initialValues }) => {
  //
  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  function handleFiltersSubmit(values, { setSubmitting }) {
    setTimeout(() => {
      setSubmitting(false);
      onChange(values);
    }, 400);
  }
  console.log('filters-init ', initialValues);

  const filters = {
    contributorId: initialValues?.contributorId ?? '',
    year: initialValues?.year ?? '',
  };

  //const { values } = useFormikContext();
  const formShape = {
    contributorId: appStore.isContributor ? YupNumeric : YupNumericReq,
    year: YupStringReq,
  };

  const validationSchema = Yup.object().shape(formShape);
  return (
    <FormFilterCard
      title={t('Κριτήρια Αναζήτησης')}
      onSubmit={handleFiltersSubmit}
      initialValues={{
        contributorId: filters?.contributorId ?? '',
        year: filters?.year,
      }}
      validationSchema={validationSchema}
    >
      <Row>
        {appStore.isPlatformOwner && <FieldSelectContributor as={FormCol4} required={true} />}
        <SelectContributionYear required={true} />
      </Row>
    </FormFilterCard>
  );
};
